'use client'

/**
 * Cookie Consent Component
 * GDPR/CCPA compliant cookie consent banner
 */

import { useState, useEffect } from 'react'
import { useConsentManagement } from '@repo/core'
import { Button, Card, HStack, VStack } from '@repo/ui'
import { CookiesRegular, DismissRegular, SettingsRegular } from '@fluentui/react-icons'
import { useTranslations } from '@repo/core/i18n'

export function CookieConsent() {
  const t = useTranslations('common.cookieConsent')
  const { consent, setConsent } = useConsentManagement()
  const [showBanner, setShowBanner] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [preferences, setPreferences] = useState({
    analytics: false,
    marketing: false,
    personalization: false,
  })

  useEffect(() => {
    // Show banner if consent hasn't been given
    if (consent === null) {
      setShowBanner(true)
    }
  }, [consent])

  const handleAcceptAll = () => {
    const fullConsent = {
      analytics: true,
      marketing: true,
      personalization: true,
    }
    setConsent(fullConsent)
    setShowBanner(false)
    setShowSettings(false)
  }

  const handleRejectAll = () => {
    const noConsent = {
      analytics: false,
      marketing: false,
      personalization: false,
    }
    setConsent(noConsent)
    setShowBanner(false)
    setShowSettings(false)
  }

  const handleSavePreferences = () => {
    setConsent(preferences)
    setShowBanner(false)
    setShowSettings(false)
  }

  const togglePreference = (key: keyof typeof preferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  if (!showBanner) {
    return null
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 sm:p-6">
      <Card className="mx-auto max-w-4xl border-2 bg-background/95 p-6 shadow-2xl backdrop-blur-sm">
        {!showSettings ? (
          // Main consent banner
          <VStack spacing="md">
            <HStack align="start" className="justify-between">
              <HStack align="start" spacing="sm">
                <CookiesRegular className="h-6 w-6 shrink-0 text-primary" />
                <div className="space-y-2">
                  <h3 className="text-lg font-semibold">{t('banner.title')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('banner.description')}
                  </p>
                </div>
              </HStack>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={handleRejectAll}
                aria-label={t('banner.rejectAriaLabel')}
              >
                <DismissRegular className="h-4 w-4" />
              </Button>
            </HStack>

            <HStack className="flex-wrap" spacing="sm">
              <Button onClick={handleAcceptAll} className="flex-1 sm:flex-initial">
                {t('actions.acceptAll')}
              </Button>
              <Button onClick={handleRejectAll} variant="outline" className="flex-1 sm:flex-initial">
                {t('actions.rejectAll')}
              </Button>
              <Button
                onClick={() => setShowSettings(true)}
                variant="outline"
                className="flex-1 sm:flex-initial"
              >
                <SettingsRegular className="mr-2 h-4 w-4" />
                {t('actions.customize')}
              </Button>
            </HStack>
          </VStack>
        ) : (
          // Settings panel
          <VStack spacing="lg">
            <HStack align="start" className="justify-between">
              <HStack align="start" spacing="sm">
                <SettingsRegular className="h-6 w-6 shrink-0 text-primary" />
                <div>
                  <h3 className="text-lg font-semibold">{t('settings.title')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('settings.description')}
                  </p>
                </div>
              </HStack>
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                onClick={() => setShowSettings(false)}
                aria-label={t('settings.closeAriaLabel')}
              >
                <DismissRegular className="h-4 w-4" />
              </Button>
            </HStack>

            <VStack spacing="md">
              {/* Essential Cookies - Always enabled */}
              <HStack align="start" className="justify-between rounded-lg border border-border p-4">
                <div className="flex-1 space-y-1">
                  <h4 className="font-medium">{t('categories.essential.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('categories.essential.description')}
                  </p>
                </div>
                <HStack>
                  <div className="rounded-full bg-primary/20 px-3 py-1 text-sm font-medium text-primary">
                    {t('categories.essential.badge')}
                  </div>
                </HStack>
              </HStack>

              {/* Analytics Cookies */}
              <HStack align="start" className="justify-between rounded-lg border border-border p-4">
                <div className="flex-1 space-y-1">
                  <h4 className="font-medium">{t('categories.analytics.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('categories.analytics.description')}
                  </p>
                </div>
                <Button
                  variant={preferences.analytics ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => togglePreference('analytics')}
                  className="shrink-0"
                >
                  {preferences.analytics ? t('states.enabled') : t('states.disabled')}
                </Button>
              </HStack>

              {/* Marketing Cookies */}
              <HStack align="start" className="justify-between rounded-lg border border-border p-4">
                <div className="flex-1 space-y-1">
                  <h4 className="font-medium">{t('categories.marketing.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('categories.marketing.description')}
                  </p>
                </div>
                <Button
                  variant={preferences.marketing ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => togglePreference('marketing')}
                  className="shrink-0"
                >
                  {preferences.marketing ? t('states.enabled') : t('states.disabled')}
                </Button>
              </HStack>

              {/* Personalization Cookies */}
              <HStack align="start" className="justify-between rounded-lg border border-border p-4">
                <div className="flex-1 space-y-1">
                  <h4 className="font-medium">{t('categories.personalization.title')}</h4>
                  <p className="text-sm text-muted-foreground">
                    {t('categories.personalization.description')}
                  </p>
                </div>
                <Button
                  variant={preferences.personalization ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => togglePreference('personalization')}
                  className="shrink-0"
                >
                  {preferences.personalization ? t('states.enabled') : t('states.disabled')}
                </Button>
              </HStack>
            </VStack>

            <HStack className="flex-wrap" spacing="sm">
              <Button onClick={handleSavePreferences} className="flex-1 sm:flex-initial">
                {t('actions.savePreferences')}
              </Button>
              <Button onClick={handleAcceptAll} variant="outline" className="flex-1 sm:flex-initial">
                {t('actions.acceptAll')}
              </Button>
              <Button onClick={handleRejectAll} variant="outline" className="flex-1 sm:flex-initial">
                {t('actions.rejectAll')}
              </Button>
            </HStack>
          </VStack>
        )}
      </Card>
    </div>
  )
}
